import React, { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import { CheckCircle2, MessageCircle, PhoneCall, PhoneOutgoing } from 'lucide-react';
import { initSocket } from '../../services/socketService';
import { hulplijnApi, HULPLIJN_WACHTRIJ_KEY, CHAT_KANALEN_KEY, type HulplijnWachtrij as Wachtrij } from '../../services/veiligeHavenApi';

function foutMelding(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
  }
  return fallback;
}

const minutenGeleden = (iso: string) => Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));

/** Wachtrij van de Directe Hulplijn; ververst op het socket-seintje van een nieuw of geclaimd verzoek. */
export function useHulplijnWachtrij() {
  const queryClient = useQueryClient();
  useEffect(() => {
    const socket = initSocket();
    if (!socket) return;
    const ververs = () => queryClient.invalidateQueries({ queryKey: HULPLIJN_WACHTRIJ_KEY });
    socket.on('hulplijn_verzoek', ververs);
    return () => {
      socket.off('hulplijn_verzoek', ververs);
    };
  }, [queryClient]);
  return useQuery({ queryKey: HULPLIJN_WACHTRIJ_KEY, queryFn: hulplijnApi.wachtrij, refetchInterval: 20_000, retry: false });
}

/**
 * Venster 1 — spoed-terugbelverzoeken uit de bedrijfspools (onderdeel 60, Onderdeel 4).
 * Een open verzoek blijft hier oplichten tot iemand uit de pool het claimt;
 * alleen wie claimt ziet het terugbelnummer, en op dat moment gaat ook het
 * chatkanaal (casusnummer) open.
 */
export const HulplijnWachtrijLijst: React.FC<{ data: Wachtrij }> = ({ data }) => {
  const { t } = useTranslation('professionalDashboard');
  const queryClient = useQueryClient();
  const ververs = () => {
    queryClient.invalidateQueries({ queryKey: HULPLIJN_WACHTRIJ_KEY });
    queryClient.invalidateQueries({ queryKey: CHAT_KANALEN_KEY });
  };
  const claim = useMutation({ mutationFn: hulplijnApi.claim, onSettled: ververs });
  const afronden = useMutation({ mutationFn: hulplijnApi.afronden, onSettled: ververs });

  if (data.open.length === 0 && data.geclaimd.length === 0) return null;

  return (
    <div className="space-y-2 mb-2">
      {data.geclaimd.map((c) => (
        <div key={c.id} className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
          <p className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
            <PhoneOutgoing className="w-4 h-4" aria-hidden="true" />
            {t('hulplijn.terugbellen', { defaultValue: 'Terugbellen — {{discipline}}', discipline: c.discipline_label })}
          </p>
          {c.terugbelnummer ? (
            <>
              <a
                href={`tel:%2331%23${c.terugbelnummer}`}
                className="mt-2 inline-flex items-center gap-2 rounded-lg bg-[#0B1F3A] px-3 py-2 text-sm font-semibold text-white"
              >
                <PhoneCall className="w-4 h-4" aria-hidden="true" /> {c.terugbelnummer}
              </a>
              <p className="mt-1.5 text-[11px] text-emerald-900/80">
                {t('hulplijn.anoniemBellen', { defaultValue: 'Bel met afgeschermd nummer: de knop zet #31# voor het nummer, zodat de medewerker uw nummer niet ziet.' })}
              </p>
            </>
          ) : (
            <p className="mt-1 text-xs text-emerald-900/80">{t('hulplijn.nummerGewist', { defaultValue: 'Het terugbelnummer is na 72 uur gewist.' })}</p>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
            {c.casus_nummer && (
              <span className="inline-flex items-center gap-1 text-emerald-900">
                <MessageCircle className="w-3.5 h-3.5" aria-hidden="true" />
                {t('hulplijn.chatCasus', { defaultValue: 'Chat open: Casus #{{nr}}', nr: c.casus_nummer })}
              </span>
            )}
            <button type="button" onClick={() => afronden.mutate(c.id)} disabled={afronden.isPending} className="font-semibold text-emerald-900 underline disabled:opacity-50">
              {t('hulplijn.afronden', { defaultValue: 'Gesprek gevoerd — afronden' })}
            </button>
          </div>
        </div>
      ))}

      {data.open.map((v) => (
        <div key={v.id} className="rounded-xl border-2 border-rose-300 bg-rose-50 p-3 animate-[pulse_2.5s_ease-in-out_infinite] motion-reduce:animate-none">
          <p className="flex items-center gap-2 text-sm font-semibold text-rose-900">
            <PhoneCall className="w-4 h-4" aria-hidden="true" />
            {t('hulplijn.spoed', { defaultValue: 'Spoed-terugbelverzoek · {{discipline}}', discipline: v.discipline_label })}
          </p>
          <p className="mt-0.5 text-xs text-rose-900/80">
            {v.bedrijfsnaam ? `${v.bedrijfsnaam} · ` : ''}
            {t('hulplijn.wachtSinds', { defaultValue: 'wacht {{n}} min', n: minutenGeleden(v.wacht_sinds) })}
          </p>
          <button
            type="button"
            onClick={() => claim.mutate(v.id)}
            disabled={claim.isPending}
            className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-rose-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-60"
          >
            <CheckCircle2 className="w-4 h-4" aria-hidden="true" /> {t('hulplijn.claim', { defaultValue: 'Claim & bel terug' })}
          </button>
        </div>
      ))}

      {claim.isError && (
        <p className="text-xs text-red-700" role="alert">
          {foutMelding(claim.error, t('hulplijn.claimFout', { defaultValue: 'Claimen lukte niet.' }))}
        </p>
      )}
    </div>
  );
};

export default HulplijnWachtrijLijst;
